import styled, { css, keyframes } from "styled-components";

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -45%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: ${(props) =>
    props.$isClosing
      ? css`
          ${fadeOut} 0.3s ease forwards
        `
      : css`
          ${fadeIn} 0.3s ease
        `};
`;

export const ModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--surface);
  border-radius: 20px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: ${slideIn} 0.3s ease-out;
  /* Fix za Chrome glitch pri brzom skrolu — translate(-50%,-50%) već pravi
     sopstveni sloj, dodajemo samo izolaciju skrola */
  overscroll-behavior: contain;

  @media (max-width: 768px) {
    width: 95%;
    margin: 20px;
  }
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px 30px 20px;
  border-bottom: 1px solid var(--accent-light);

  @media (max-width: 768px) {
    padding: 20px 20px 15px;
  }
`;

export const ModalTitle = styled.h3`
  color: var(--accent);
  font-size: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 18px;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: var(--background-soft);
    color: var(--text);
    transform: scale(1.1);
  }
`;

export const ModalContent = styled.div`
  padding: 0 30px 30px;

  @media (max-width: 768px) {
    padding: 0 20px 20px;
  }
`;

export const Instructions = styled.div`
  background: var(--gradient-primary);
  color: var(--surface);
  padding: 20px;
  border-radius: 15px;
  margin-bottom: 25px;

  p {
    margin-bottom: 15px;
    line-height: 1.6;
  }

  p:last-of-type {
    margin-bottom: 0;
    font-size: 13px;
    opacity: 0.9;
  }
`;

export const InstructionTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
  font-size: 16px;
  font-weight: 600;
`;

export const InstructionList = styled.ol`
  margin: 15px 0;
  padding-left: 20px;
`;

export const InstructionItem = styled.li`
  margin-bottom: 8px;
  line-height: 1.5;
`;

export const FormGroup = styled.div`
  margin-bottom: 20px;
`;

export const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text);
  font-weight: 500;
  margin-bottom: 8px;
  font-size: 14px;
`;

export const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 2px solid var(--accent-light);
  border-radius: 10px;
  font-family: inherit;
  font-size: 14px;
  background: var(--background-soft);
  color: var(--text);
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(122, 14, 51, 0.1);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 15px;
  border: 2px solid var(--accent-light);
  border-radius: 10px;
  font-family: inherit;
  font-size: 14px;
  background: var(--background-soft);
  color: var(--text);
  transition: all 0.3s ease;
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(122, 14, 51, 0.1);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

export const CancelButton = styled.button`
  background: var(--background-soft);
  color: var(--text);
  border: 2px solid var(--accent-light);
  padding: 12px 25px;
  border-radius: 10px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: var(--background);
    transform: translateY(-1px);
  }
`;

export const SubmitButton = styled.button`
  background: var(--gradient-primary);
  color: var(--surface);
  border: none;
  padding: 12px 25px;
  border-radius: 10px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(122, 14, 51, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

export const CustomUploadButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: var(--gradient-primary);
  color: var(--surface);
  border: none;
  border-radius: 10px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(122, 14, 51, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    font-size: 14px;
  }
`;

export const ImagePreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 15px;
  margin-top: 15px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    gap: 10px;
  }
`;

export const ImagePreviewItem = styled.div`
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  border: 2px solid var(--accent-light);
  background: var(--background-soft);
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--accent);
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

export const PreviewImage = styled.img`
  width: 100%;
  height: 100px;
  object-fit: cover;
  display: block;

  @media (max-width: 768px) {
    height: 80px;
  }
`;

export const DeleteImageButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #dc2626;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: scale(1.1);
    color: #b91c1c;
  }
`;

export const ImageName = styled.p`
  padding: 8px;
  margin: 0;
  font-size: 11px;
  color: var(--text-muted);
  text-align: center;
  word-break: break-all;
  line-height: 1.2;
  background: var(--background-soft);
`;

export const SelectedImagesInfo = styled.div`
  margin-top: 10px;
  padding: 10px 15px;
  background: var(--background-soft);
  border-radius: 8px;
  border: 2px solid var(--accent-light);

  p {
    margin: 0;
    font-size: 13px;
    color: var(--text);

    strong {
      color: var(--accent);
    }
  }
`;
